import { HTMLAttributes, useEffect, useState } from 'react';
import {
  AmericanCountryList,
  BUSINESS_INSURANCE_TYPE_LIST,
  BUSINESS_INSURANCE_TYPE_MAP,
  Checkbox,
  CountrySelectInput,
  EuropeanUnionList,
  Input,
  InsuranceType,
  LoadingMask,
  Modal,
  PhoneNumberInput,
  PolicyType,
  QuoteProcessTypeEnum,
  Select,
  UKCountryList,
  apiCreateQuote,
  apiDiscardedGitNewCreateQuote,
  apiNewCreateQuote,
  convertDayjsTimeToServerStoredTimeData,
  getBrokerQuotingThroughMap,
  getDeductionMethodMap,
  getGenerateQuoteMethodList,
  getGenerateQuoteMethodMap,
  getRecruitmentCoverTypeMap,
  useLocalTranslation,
} from '@jove-frontend/uikit';
import clsx from 'clsx';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import UploadQuoteFile from './components/uploadQuoteFile';

export type ResumeQuoteDialogProps = HTMLAttributes<HTMLDivElement> & {
  close?: () => void;
  success?: (info?: { id: string }) => void;
};

const CreateQuoteDialog = (props: ResumeQuoteDialogProps) => {
  const { close, success } = props;
  const { $t } = useLocalTranslation();
  const methods = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      country: 'GB',
      insuranceType: BUSINESS_INSURANCE_TYPE_MAP.goodsInTransit.value,
    },
  });
  const {
    setValue,
    register,
    formState: { errors },
    watch,
    getValues,
    handleSubmit,
  } = methods;
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [quoteCreateMode, setQuoteCreateMode] = useState(getGenerateQuoteMethodMap().manual.value);
  const [skipSignature, setSkipSignature] = useState(false);
  const [allowCreateCountries, setAllowCreateCountries] = useState([...UKCountryList, ...AmericanCountryList]);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);

  /**
   * request
   */
  const requestCreateQuote = async () => {
    const { firstName, lastName, email, phone, country, insuranceType } = getValues();
    const params = {
      firstName,
      lastName,
      email,
      phone,
      country,
      businessCountry: country,
      insuranceType: insuranceType as InsuranceType,
      meta: {
        skip_sign: skipSignature,
        isOldIncorrectBusinessInsurance: false,
      },
      policyType: PolicyType.ANNUAL,
      billingCycle: getDeductionMethodMap().annual.value,
      projectPeriodStart: convertDayjsTimeToServerStoredTimeData(new Date()),
      source: getBrokerQuotingThroughMap().brokerPortal.value,
      coverType: getRecruitmentCoverTypeMap().pickAndChoose.value,
      turnOnCombinedPackage: false,
    };

    let result = void 0;
    setLoading(true);
    if (insuranceType === BUSINESS_INSURANCE_TYPE_MAP.goodsInTransit.value) {
      params['businessActivity'] = 'BCS-047';
      params['incomeOrigin'] = { GB: 100 };
      if (AmericanCountryList.includes(country)) {
        params.meta['newQuoteFlowType'] = QuoteProcessTypeEnum.Normal;
      }
      result = await apiDiscardedGitNewCreateQuote(params as any);
    } else if (insuranceType === BUSINESS_INSURANCE_TYPE_MAP.businessInsurance.value) {
      if (UKCountryList.includes(country)) {
        // Chubb (Abandoned)
        // params.policyType = PolicyType.ANNUAL_FLEXIBLE;
      }
      if (EuropeanUnionList.includes(country)) {
        // Accelerant
        params.billingCycle = getDeductionMethodMap().monthly.value;
        params.meta['newQuoteFlowType'] = QuoteProcessTypeEnum.Normal;
      }

      if (UKCountryList.includes(country)) {
        // Origin chubb create request:
        result = await apiCreateQuote(params);
      } else {
        result = await apiNewCreateQuote(params);
      }
    }
    setLoading(false);
    if (result?.[0]) {
      success?.(result?.[0]);
      closeAction();
    }
  };

  /**
   * watch
   */
  const insuranceTypeWatch = watch('insuranceType');
  const phoneWatch = watch('phone');
  const countryWatch = watch('country');
  const allWatch = watch();

  /**
   * effects
   */
  useEffect(() => {
    const { firstName, lastName, email, phone, country } = allWatch;
    setButtonDisabled(!firstName || !lastName || !email || !phone);
  }, [allWatch]);

  useEffect(() => {
    if (insuranceTypeWatch === BUSINESS_INSURANCE_TYPE_MAP.goodsInTransit.value) {
      setAllowCreateCountries([...UKCountryList, ...AmericanCountryList]);
    } else if (insuranceTypeWatch === BUSINESS_INSURANCE_TYPE_MAP.businessInsurance.value) {
      setAllowCreateCountries([...UKCountryList, ...AmericanCountryList, ...EuropeanUnionList]);
    }
  }, [insuranceTypeWatch]);

  /**
   * methods
   */
  const closeAction = () => {
    close?.();
  };

  const insuranceTypeChangeAction = (e) => {
    setValue('insuranceType', e.target.value);
  };

  const phoneChangeAction = (value) => {
    setValue('phone', value);
  };

  const countryChangeAction = (value) => {
    setValue('country', value);
    setValue('phone', '');
  };

  const creatQuoteAction = () => {
    handleSubmit(requestCreateQuote)();
  };

  const uploadFileCreateQuoteSuccessAction = (item) => {
    toast.success($t('common:created_successfully'));
    success?.(item);
    closeAction();
  };

  const uploadingFileCreateQuoteAction = (loading) => {
    setUploadFileLoading(loading);
  };

  return (
    <Modal isOpen={true} onClose={closeAction} variant="plain" title="" size="2xl">
      <div className="flex flex-col overflow-auto pl-4">
        {loading && <LoadingMask className="absolute z-[100]" />}
        <p className="text-black-light mb-3 text-3xl font-black">{$t('pages:quotes.create_quote')}</p>
        <FormProvider {...methods}>
          <div className="my-3 flex w-full flex-row gap-1 rounded-lg bg-[#F3F4F6] p-1">
            {getGenerateQuoteMethodList().map((item) => {
              return (
                <div
                  key={item.value}
                  onClick={uploadFileLoading ? null : () => setQuoteCreateMode(item.value)}
                  className={`flex-1 rounded-md px-2 py-2 text-center ${
                    quoteCreateMode === item.value ? 'bg-primary-black text-white' : 'text-[#4B5563]'
                  } ${uploadFileLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <p className="text-center text-sm font-bold leading-5">{item.label}</p>
                </div>
              );
            })}
          </div>
          {quoteCreateMode === getGenerateQuoteMethodMap().manual.value && (
            <>
              <Select
                label={$t('common:insurance_type')}
                value={insuranceTypeWatch}
                options={BUSINESS_INSURANCE_TYPE_LIST.filter((item) => item.value !== BUSINESS_INSURANCE_TYPE_MAP.hiredInPlant.value)}
                onChange={insuranceTypeChangeAction}
              />
              <div className="my-3 flex flex-row">
                <Input
                  className="mr-2 w-full"
                  {...register('firstName', { required: true })}
                  name="firstName"
                  label={$t('common:first_name')}
                  error={!!errors.firstName}
                  error_message={$t('common:please_input')}
                />
                <Input
                  className="w-full"
                  {...register('lastName', { required: true })}
                  name="lastName"
                  label={$t('common:last_name')}
                  error={!!errors.lastName}
                  error_message={$t('common:please_input')}
                />
              </div>
              <Input
                className="mb-3"
                {...register('email', { required: true })}
                name="email"
                type="email"
                label={$t('common:email')}
                error={!!errors.email}
                error_message={$t('common:please_input')}
              />
              <CountrySelectInput
                {...register('country', { required: true })}
                className="mb-3"
                name="country"
                label={$t('common:country')}
                countries={allowCreateCountries}
                value={countryWatch}
                onChange={countryChangeAction}
                error={!!errors.country}
                error_message={$t('common:please_select')}
              />
              <PhoneNumberInput
                {...register('phone', { required: true })}
                className="mb-3"
                name="phone"
                id="phone"
                key={countryWatch || 'GB'}
                value={phoneWatch}
                onChange={phoneChangeAction}
                label={$t('common:phone_number')}
                autoComplete="off"
                error={!!errors.phone}
                error_message={$t('common:please_input')}
                country={countryWatch as any}
              />
              <Checkbox
                label={$t('common:skip_signature')}
                value={'skipSignature'}
                checked={skipSignature}
                onChange={() => setSkipSignature((prev) => !prev)}
              />
            </>
          )}
          {quoteCreateMode === getGenerateQuoteMethodMap().uploadFiles.value && (
            <UploadQuoteFile success={uploadFileCreateQuoteSuccessAction} uploadingFile={uploadingFileCreateQuoteAction} />
          )}
        </FormProvider>
        {quoteCreateMode === getGenerateQuoteMethodMap().manual.value && (
          <div className="mt-6 flex justify-end space-x-3">
            <button className="mr-2 text-lg font-semibold" onClick={closeAction}>
              {$t('common:cancel')}
            </button>
            <button
              disabled={buttonDisabled}
              className={clsx(
                'flex items-center justify-start rounded-full px-6 py-2 text-xl font-bold text-white',
                `${buttonDisabled ? 'bg-gray-info' : 'bg-brand-secondary'}`
              )}
              onClick={creatQuoteAction}
            >
              {$t('common:create')}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CreateQuoteDialog;
